import { Col, DatePicker, Form, message, Row, Space } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../../components/DraggableModal";
import { WinterCerealFertilizerItems } from "./WinterCerealFertilizerItems";
import {
  EntWinterCerealFertilizer,
  WinterCerealFertilizerDetails
} from "../../../../models/chemicalization/winterCerealFertilizer";
import dayjs, { Dayjs } from "dayjs";
import { dateFormat } from "../../../../utils";
import { SelectTerritorialUnits } from "../../../../components/selects";

enum FormField {
  id,
  periodStart,
  periodEnd,
  territorialUnitId,
}

interface IProps {
  show: boolean;
  winterCerealFertilizer: EntWinterCerealFertilizer;
  onHide: () => void;
  onSubmit: (pos: EntWinterCerealFertilizer) => Promise<boolean>;
}

export const AddWinterCerealFertilizer = (props: IProps) => {
  const { t } = useTranslation();
  const [ form ] = useForm();
  const [ loading, setLoading ] = useState<boolean>(false);
  const [ startDate, setStartDate ] = useState<Dayjs | null>(null);
  const [ items, setItems ] = useState<WinterCerealFertilizerDetails[] | null>(null);

  useEffect(() => {
	if (props.show) {
	  if (props.winterCerealFertilizer.id === null) {
		setItems(null);
	  }
	  setFieldsFromEntity();
	}
  }, [props.show]);


  const setFieldsFromEntity = (): void => {
	form.setFields([
	  {
		name: FormField.periodStart,
		value: props.winterCerealFertilizer.periodStart === null ? undefined : dayjs(props.winterCerealFertilizer.periodStart)
	  },
	  {
		name: FormField.periodEnd,
		value: props.winterCerealFertilizer.periodEnd === null ? undefined : dayjs(props.winterCerealFertilizer.periodEnd)
	  },
	  { name: FormField.territorialUnitId, value: props.winterCerealFertilizer.territorialUnit?.id },
	]);
	if (props.winterCerealFertilizer.winterCerealFertilizerDetails !== null) {
	  setItems(props.winterCerealFertilizer.winterCerealFertilizerDetails)
	}
  }


  const setEntityFromFields = (): EntWinterCerealFertilizer => {
	let res = _.cloneDeep(props.winterCerealFertilizer);
	res.periodStart = form.getFieldValue(FormField.periodStart)?.format("YYYY-MM-DD");
	res.periodEnd = form.getFieldValue(FormField.periodEnd)?.format("YYYY-MM-DD");
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnitId);

	res.winterCerealFertilizerDetails = items;
	return res;
  };

  const hideModal = () => {
	props.onHide();
	setItems(null)
  };

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  const data = setEntityFromFields();
	  const { winterCerealFertilizerDetails } = data;

	

	  if (!winterCerealFertilizerDetails || winterCerealFertilizerDetails.length === 0) {
		throw new Error("Список не должен быть пустым!");
	  }

	  const invalidRegisters = winterCerealFertilizerDetails.filter(
		(winterCerealFertilizer: WinterCerealFertilizerDetails) =>
		  winterCerealFertilizer.territorialUnitDistrictId === null ||
		  winterCerealFertilizer.neededPKg === null ||
		  winterCerealFertilizer.neededKKg === null ||
		  winterCerealFertilizer.totalSownArea === null ||
		  winterCerealFertilizer.factSownArea === null ||
		  winterCerealFertilizer.fertilizedArea === null
	  );

	  if (invalidRegisters.length > 0) {
		throw new Error("У всех элементов должны быть заполнены!");
	  }

	  const result = await props.onSubmit(data);
	  if (result) hideModal();
	} catch (error: any) {
	  message.error(error.message || "Произошла ошибка при отправке формы.");
	} finally {
	  setLoading(false);
	}
  };

  return (
	<DraggableModal
	  title={
		props.winterCerealFertilizer.id === null
		  ? t("general.add")
		  : t("general.edit")
	  }
	  visible={ props.show }
	  onCancel={ props.onHide }
	  onSubmit={ handleSubmit }
	  submitLoading={ loading }

	  width={ 800 }
	>
	  <Form
		initialValues={ { remember: true } }
		autoComplete="off"
		layout="vertical"
		form={ form }>
		  <Row style={ { margin: 0 } } gutter={ 12 }>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('titles.territorialUnitRegion') }
				name={ FormField.territorialUnitId }
				rules={ [ { required: true, message: t("msg.fill_in_the_field") } ] }
			  >
				<SelectTerritorialUnits type={ ['REGION'] }/>
			  </Form.Item>
			</Col>
			<Col className="gutter-row" span={ 12 }>
			  <Space.Compact>
				<Form.Item
				  label={ t('general.start_date_period') }
				  name={ FormField.periodStart }
				  rules={ [ { required: true, message: t("msg.fill_in_the_field") } ] }
				>
				  <DatePicker
					format={ dateFormat }
					placeholder={ t('general.select_date') } onChange={ (date) => setStartDate(date) }/>

				</Form.Item>
				<Form.Item
				  label={ t('general.end_date_period') }
				  name={ FormField.periodEnd }
				  rules={ [ { required: true, message: t("msg.fill_in_the_field") } ] }
				>
				  <DatePicker
					format={ dateFormat }
					placeholder={ t('general.select_date') }
					disabledDate={ (current) =>
					  !!startDate && current.isBefore(startDate.add(1, "day"), "day")
					}
				  />
				</Form.Item>
			  </Space.Compact>
			</Col>
			<Col className="gutter-row" span={ 24 }>
			  <WinterCerealFertilizerItems setItems={ setItems } items={ items }/>
			</Col>
		  </Row>
	  </Form>
	</DraggableModal>
);
};
